import { useState, useEffect } from 'react';
import useTheme from './useTheme';

/**
 * Custom hook for managing resizable panels in the dashboard
 * @returns {Object} State and handlers for panel resizing
 */
const useResizablePanels = () => {
  // Get current theme context
  const { theme } = useTheme();
  
  // Calculate 51% of viewport height for default top row height (reduced from 60% to give 15% more to bottom row)
  const defaultTopRowHeight = Math.round(window.innerHeight * 0.51);
  
  // State for panel dimensions
  const [marketsPanelWidth, setMarketsPanelWidth] = useState(Math.round(window.innerWidth * 0.35));
  const [tradeDeskPanelWidth, setTradeDeskPanelWidth] = useState(300);
  const [topRowHeight, setTopRowHeight] = useState(defaultTopRowHeight);
  const [activityPanelWidth, setActivityPanelWidth] = useState(500);
  
  // Track if the user has manually resized panels
  const [hasResizedMarkets, setHasResizedMarkets] = useState(false);
  
  // CSS variable update
  useEffect(() => {
    document.documentElement.style.setProperty('--markets-width', `${marketsPanelWidth}px`);
    document.documentElement.style.setProperty('--tradedesk-width', `${tradeDeskPanelWidth}px`);
    document.documentElement.style.setProperty('--top-row-height', `${topRowHeight}px`);
    document.documentElement.style.setProperty('--activity-width', `${activityPanelWidth}px`);
  }, [marketsPanelWidth, tradeDeskPanelWidth, topRowHeight, activityPanelWidth]);
  
  // Handle window resize
  useEffect(() => {
    const handleWindowResize = () => {
      // Calculate the maximum markets panel width (35% of viewport)
      const maxMarketWidth = Math.round(window.innerWidth * 0.35);
      
      // Calculate the default top row height (51% of viewport)
      const defaultHeight = Math.round(window.innerHeight * 0.51);
      
      // Update the markets panel width to the maximum only if user hasn't manually resized
      if (!hasResizedMarkets) {
        setMarketsPanelWidth(maxMarketWidth);
      } else {
        // If user has manually resized, maintain the same proportion relative to window width
        const proportion = marketsPanelWidth / window.innerWidth;
        setMarketsPanelWidth(Math.round(window.innerWidth * proportion));
      }
      
      // Update the top row height
      setTopRowHeight(defaultHeight);
    };
    
    // Add resize event listener
    window.addEventListener('resize', handleWindowResize);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [hasResizedMarkets, marketsPanelWidth]);

  // Handle Markets panel resize
  const handleMarketsResize = (e) => {
    e.preventDefault();
    const startWidth = marketsPanelWidth;
    const startX = e.clientX;
    
    // Calculate viewport-based constraints
    const maxWidth = Math.round(window.innerWidth * 0.35); // 35% of viewport width
    const minWidth = 390; // Minimum width to fit two 190px cards plus grid gap
    
    const onMouseMove = (moveEvent) => {
      moveEvent.preventDefault();
      const newWidth = startWidth + (moveEvent.clientX - startX);
      
      // Only allow reducing the width, not increasing it beyond maxWidth
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setMarketsPanelWidth(newWidth);
        // Mark that user has manually resized
        if (!hasResizedMarkets) {
          setHasResizedMarkets(true);
        }
      }
    };
    
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Function to reset Markets panel to maximum width
  const resetMarketsPanelWidth = () => {
    const maxWidth = Math.round(window.innerWidth * 0.35);
    setMarketsPanelWidth(maxWidth);
    setHasResizedMarkets(false);
  };

  // Expose the reset function globally
  useEffect(() => {
    window.resetMarketsPanelWidth = resetMarketsPanelWidth;
    
    return () => {
      window.resetMarketsPanelWidth = undefined;
    };
  }, []);

  // Handle TradeDesk panel resize
  const handleTradeDeskResize = (e) => {
    e.preventDefault();
    const startWidth = tradeDeskPanelWidth;
    const startX = e.clientX;
    
    // Calculate container width to set percentage-based constraints
    const rightSideWidth = document.querySelector('.right-side').offsetWidth;
    const minWidth = Math.max(200, rightSideWidth * 0.2); // At least 20% or 200px
    const maxWidth = Math.min(600, rightSideWidth * 0.4); // At most 40% or 600px
    
    const onMouseMove = (moveEvent) => {
      moveEvent.preventDefault();
      const newWidth = startWidth + (moveEvent.clientX - startX);
      
      // Constrain width based on calculated min/max values
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        // Update the CSS variable directly to ensure immediate update
        setTradeDeskPanelWidth(newWidth);
        
        // Calculate the percentage of the container this represents
        const percentage = Math.round((newWidth / rightSideWidth) * 100);
        
        // Update grid template columns directly for immediate effect
        const rightSide = document.querySelector('.right-side');
        if (rightSide) {
          rightSide.style.gridTemplateColumns = `${percentage}% ${100-percentage}%`;
        }
      }
    };
    
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Handle Top Row resize (Graph and other top panels height)
  const handleTopRowResize = (e) => {
    e.preventDefault();
    const startHeight = topRowHeight;
    const startY = e.clientY;
    
    // Calculate viewport-based constraints
    const minHeight = Math.round(window.innerHeight * 0.4); // Minimum 40% of viewport height
    const maxHeight = Math.round(window.innerHeight * 0.7); // Maximum 70% of viewport height
    
    const onMouseMove = (moveEvent) => {
      moveEvent.preventDefault();
      const newHeight = startHeight + (moveEvent.clientY - startY);
      // Constrain height using viewport-based values
      if (newHeight >= minHeight && newHeight <= maxHeight) {
        setTopRowHeight(newHeight);
      }
    };
    
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Handle Activity panel resize
  const handleActivityResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const startX = e.clientX;
    
    // Get the bottom row width to calculate percentages
    const bottomRow = document.querySelector('.bottom-row');
    const bottomRowWidth = bottomRow ? bottomRow.offsetWidth : 0;
    
    // Current percentage (starts at 66.67%)
    let currentPercentage = 66.67;
    
    const onMouseMove = (moveEvent) => {
      moveEvent.preventDefault();
      moveEvent.stopPropagation();
      
      // Calculate delta movement and convert to percentage
      const deltaX = moveEvent.clientX - startX;
      const deltaPercentage = (deltaX / bottomRowWidth) * 100;
      
      // Calculate new percentage for activity panel
      let newPercentage = currentPercentage + deltaPercentage;
      
      // Constrain percentage (50% to 75%)
      newPercentage = Math.max(50, Math.min(75, newPercentage));
      
      // Update the grid template columns directly for immediate effect
      if (bottomRow) {
        const marginPercentage = 100 - newPercentage;
        bottomRow.style.gridTemplateColumns = `${newPercentage}% ${marginPercentage}%`;
        
        // Update current percentage for next move
        currentPercentage = newPercentage;
        
        // Reset start X for continuous movement
        e.clientX = moveEvent.clientX;
      }
    };
    
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Return state and handlers
  return {
    // Dimensions
    marketsPanelWidth,
    tradeDeskPanelWidth,
    topRowHeight,
    activityPanelWidth,
    
    // Resize handlers
    handleMarketsResize,
    handleTradeDeskResize,
    handleTopRowResize,
    handleActivityResize,
    
    // Reset function
    resetMarketsPanelWidth,
    
    // Current theme
    currentTheme: theme
  };
};

export default useResizablePanels; 